import { useCallback, useEffect, useRef, useState } from "react"

import { Button, Form, FormInstance, Input } from "antd"
import { NOTIFICATION_TYPES, showNotification } from "features/common/notifications"
import { getUserAvatarUrl, getUserLocalAvatarUrl, userHasPasswordSelector, userSelector } from 'features/user/model/selectors/userSelectors'
import { changePasswordThunk, deleteAvatarThunk, uploadAvatarThunk, userActions } from "features/user/model/slice/userSlice"
import _ from "lodash"
import { useAppDispatch, useAppSelector } from "shared/config/store"
import styled from "styled-components"

import { fetchFamilyThunk } from '../../team/model/slice/teamSlice'

import { PasswordInput } from "./PasswordInput"
import { ProfileAvatar } from './ProfileAvatar'

// import { PasswordInput } from "./PasswordInput"


export const ProfileForm = ({
  initialValues,
  onSubmit,
  loading,
}: {
  initialValues: {
    name: string;
    email: string;
    avatarUrl?: string;
  };
  onSubmit: (form: FormInstance<any>, values: any) => void;
  loading?: boolean;
}) => {
  const [form] = Form.useForm()
  const avatarFile = useRef<File | null>(null)
  const [isAvatarAvailable, setIsAvatarAvailable] = useState(false)
  const abortControllerRef = useRef<AbortController | null>(null)
  const [nameTouched, setNameTouched] = useState(false)
  const avatarUrl = useAppSelector(getUserAvatarUrl)
  const localAvatarUrl = useAppSelector(getUserLocalAvatarUrl)
  const hasPassword = useAppSelector(userHasPasswordSelector)
  const user = useAppSelector(userSelector)
  const dispatch = useAppDispatch()
  const [avatarLoading, setAvatarLoading] = useState(false)
  const [shouldAvatarDelete, setShouldAvatarDelete] = useState(false)
  const successNotifiedRef = useRef(false)

  const notifySuccessOnce = useCallback(() => {
    if (!successNotifiedRef.current) {
      showNotification({
        message: 'All set! Changes saved',
        type: NOTIFICATION_TYPES.SUCCESS,
      })
      successNotifiedRef.current = true
    }
  }, [])

  useEffect(() => {
    if (localAvatarUrl || avatarUrl) {
      setIsAvatarAvailable(true)
    }
  }, [localAvatarUrl, avatarUrl])

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  const isSaveDisabled = useCallback((form: FormInstance<any>) => {
    const { name = '', email = '' } = form.getFieldsValue(['name', 'email'])
    const currentClean = { name: (name ?? '').trim(), email: email ?? '' }
    const initialClean = {
      name: (initialValues?.name ?? '').trim(),
      email: initialValues?.email ?? ''
    }
    const hasFormChanges = !_.isEqual(currentClean, initialClean)

    const hasFieldsError = !!form.getFieldsError().filter(({ errors }) => errors.length).length
  
    const currentPassword = form.getFieldValue('currentPassword')
    const newPassword = form.getFieldValue('newPassword')
    const confirmPassword = form.getFieldValue('confirmPassword')
  
    const anyPasswordFilled = hasPassword && (!!currentPassword || !!newPassword || !!confirmPassword)
    const allPasswordFilled = hasPassword && !!currentPassword && !!newPassword && !!confirmPassword
  
    const hasAvatarChange = !!localAvatarUrl || shouldAvatarDelete
    
    const hasMeaningfulChange = hasFormChanges || hasAvatarChange || allPasswordFilled
  
    return (
      loading ||
      hasFieldsError ||
      (anyPasswordFilled && !allPasswordFilled) ||
      !hasMeaningfulChange
    )
  }, [loading, localAvatarUrl, shouldAvatarDelete, hasPassword, initialValues])  

  const handleSubmit = async (form: FormInstance<any>, values: any) => {
    successNotifiedRef.current = false
    // Проверяем, изменились ли значения формы
    const valuesChanged = !_.isEqual(
      _.pick(values, Object.keys(initialValues)),
      _.pick(initialValues, Object.keys(initialValues))
    )

    // Если значения изменились, отправляем форму
    if (valuesChanged) {
      await onSubmit(form, values)
      form.resetFields(['name'])
    }

    if (hasPassword && values.currentPassword && values.newPassword && values.confirmPassword) {
      try {
        await dispatch(changePasswordThunk({
          userId: user?.id || 0,
          password: values.newPassword,
          current_password: values.currentPassword
        })).unwrap()

        notifySuccessOnce()

        form.setFieldsValue({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        })
        form.resetFields(['currentPassword', 'newPassword', 'confirmPassword'])
      } catch (error: any) {
        const message =
          error?.detail?.current_password === 'Current password is incorrect'
            ? 'Looks like the current password is incorrect.'
            : 'Something went wrong. Please try again'
        showNotification({
          message,
          type: NOTIFICATION_TYPES.ERROR,
        })
      }
    }

    // Если есть новый аватар, отправляем его
    if (isAvatarAvailable) {
      handleAvatarUpload()
    }

    // Если аватар был удален, удаляем его
    if (shouldAvatarDelete) {
      setAvatarLoading(true)
      const abortController = new AbortController()
      try {
        await dispatch(deleteAvatarThunk({ signal: abortController.signal })).unwrap()
        notifySuccessOnce()
        dispatch(userActions.setAvatarUrl(null))
        dispatch(userActions.setLocalAvatarUrl(null))
        dispatch(fetchFamilyThunk())
        setShouldAvatarDelete(false)
      } catch (e) {
        showNotification({
          message: 'Failed to delete avatar',
          type: NOTIFICATION_TYPES.ERROR,
        })
      } finally {
        setAvatarLoading(false)
      }
    }
  }

  const handleBeforeUpload = (file: File) => {
    avatarFile.current = file
    setIsAvatarAvailable(!!avatarFile.current)
    setShouldAvatarDelete(false)
  }

  const handleAvatarUploadSuccess = () => {
    avatarFile.current = null
    setIsAvatarAvailable(false)
    URL.revokeObjectURL(localAvatarUrl)
    dispatch(userActions.setLocalAvatarUrl(null))
    dispatch(fetchFamilyThunk())
    showNotification({
      message: 'All set! Changes saved',
      type: NOTIFICATION_TYPES.SUCCESS,
    })
  }

  const handleAvatarUploadError = (error: any) => {
    if (
      error?.code === "ValidationError" &&
      error?.detail?.file_params?.size
    ) {
      showNotification({
        message: 'File is too large. Max size is 5MB.',
        type: NOTIFICATION_TYPES.ERROR,
      })
      return
    } else {
      showNotification({
        message: 'Failed to upload avatar',
        type: NOTIFICATION_TYPES.ERROR,
      })
    }
  }

  const handleAvatarUpload = () => {
    if (avatarFile.current) {
      setAvatarLoading(true)
      dispatch(uploadAvatarThunk(avatarFile.current))
        .unwrap()
        .then(handleAvatarUploadSuccess)
        .catch(handleAvatarUploadError)
        .finally(() => {
          setAvatarLoading(false)
        })
    }
  }

  const handleAvatarDelete = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    abortControllerRef.current = new AbortController()
    setShouldAvatarDelete(true)
    setIsAvatarAvailable(false)
  }

  const handleNameFocus = () => {
    setNameTouched(true)
  }

  const handleNameBlur = () => {
    setNameTouched(true)
    
    // Check if the name is empty or consists of only spaces
    const currentName = form.getFieldValue('name')
    if (!currentName || currentName.trim() === '') {
      // Restore value from initialValues
      form.setFieldValue('name', initialValues.name)
    }
  }

  const validatePassword = (value: string) => {
    if (!value) return undefined

    const trimmedValue = value.trim()
    if (!trimmedValue) return "Oops! The password is empty or invalid."

    return undefined
  }


  return (
    <StyledFormWrapper>
      <Form
        form={form}
        layout="vertical"
        initialValues={{ ...initialValues }}
        onFinish={handleSubmit.bind(null, form)}
      >
        <Form.Item>
          <ProfileAvatar
            loading={avatarLoading}
            localAvatarUrl={isAvatarAvailable ? localAvatarUrl : null}
            avatarUrl={isAvatarAvailable ? avatarUrl : null}
            onUpload={handleAvatarUpload}
            onBeforeUpload={handleBeforeUpload}
            onDelete={handleAvatarDelete}
          />
        </Form.Item>

        <Form.Item
          label="Name"
          name="name"
          required={false}
          validateTrigger={nameTouched ? "onChange" : "onSubmit"}
          rules={[
            { whitespace: true, message: "Name cannot consist of only spaces" }
          ]}
        >
          <Input
            maxLength={50}
            onFocus={handleNameFocus}
            onBlur={handleNameBlur}
          />
        </Form.Item>

        <Form.Item label="Email"
          name="email"
        >
          <Input
            disabled
            readOnly
            style={{ background: "#f5f5f5" }}
          />
        </Form.Item>

        {hasPassword && (
          <>
            <Form.Item
              label="Current password"
              name="currentPassword"
              validateTrigger={["onBlur"]}
              rules={[
                ({ getFieldValue }) => ({
                  validator: (_, value) => {
                    const newPasswordVal = getFieldValue("newPassword")
                    const confirmPasswordVal = getFieldValue("confirmPassword")
                    const anyPasswordFilled = !!value || !!newPasswordVal || !!confirmPasswordVal
                    if (!anyPasswordFilled) {
                      return Promise.resolve()
                    }
                    const error = validatePassword(value)
                    if (error) {
                      return Promise.reject(new Error(error))
                    }
                    return Promise.resolve()
                  }
                })
              ]}
            >
              <PasswordInput
                placeholder="Your password"
                onChange={() => {
                  form.setFields([{ name: 'currentPassword', errors: [] }])
                }}
              />
            </Form.Item>

            <Form.Item
              label="New password"
              name="newPassword"
              validateTrigger={["onBlur"]}
              rules={[
                ({ getFieldValue }) => ({
                  validator: (_, value) => {
                    const currentPasswordVal = getFieldValue("currentPassword")
                    const confirmPasswordVal = getFieldValue("confirmPassword")
                    const anyPasswordFilled = !!currentPasswordVal || !!value || !!confirmPasswordVal
                    if (!anyPasswordFilled) {
                      return Promise.resolve()
                    }
                    const error = validatePassword(value)
                    if (error) {
                      return Promise.reject(new Error(error))
                    }
                    return Promise.resolve()
                  }
                })
              ]}
            >
              <PasswordInput
                placeholder="Your password"
                onChange={() => {
                  form.setFields([
                    { name: 'newPassword', errors: [] },
                    { name: 'confirmPassword', errors: [] },
                  ])
                }}
              />
            </Form.Item>

            <Form.Item
              label="Confirm new password"
              name="confirmPassword"
              validateTrigger={["onBlur"]}
              rules={[
                ({ getFieldValue }) => ({
                  validator: (_, value) => {
                    const currentPasswordVal = getFieldValue("currentPassword")
                    const newPasswordVal = getFieldValue("newPassword")
                    const anyPasswordFilled = !!currentPasswordVal || !!newPasswordVal || !!value
                    if (!anyPasswordFilled) {
                      return Promise.resolve()
                    }
                    const error = validatePassword(value)
                    if (error) {
                      return Promise.reject(new Error(error))
                    }
                    return Promise.resolve()
                  }
                }),
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("newPassword") === value) {
                      return Promise.resolve()
                    }
                    return Promise.reject(
                      new Error("Oops! The passwords don't match")
                    )
                  },
                }),
              ]}
            >
              <PasswordInput
                placeholder="Your password"
                onChange={() => {
                  form.setFields([{ name: 'confirmPassword', errors: [] }])
                }}
              />
            </Form.Item>
          </>
        )}

        <Form.Item shouldUpdate>
          {() => (
            <StyledSaveButton
              type="primary"
              htmlType="submit"
              loading={loading}
              disabled={isSaveDisabled(form)}
            >
              {loading ? "Saving..." : "Save changes"}
            </StyledSaveButton>
          )}
        </Form.Item>
      </Form>
    </StyledFormWrapper>
  )
}

const StyledFormWrapper = styled.div`
  max-width: 480px;
  margin: 24px auto 0 0;
`

export const StyledSaveButton = styled(Button)`
  border-radius: 6px;
  font-weight: 400;
  font-size: 14px;
  line-height: 22px;
  padding: 5px 15px;
  margin-top: 8px;
`
